
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Upload, Download, ExternalLink } from 'lucide-react';
import { UploadedDocumentItem } from './UploadedDocumentItem';

interface RequiredDocument {
  id: string;
  title: string;
  description: string;
  required: boolean;
  sampleUrl?: string;
  templateUrl?: string;
  uploadedDocs: {
    id: string;
    name: string;
    uploadedAt: string;
    relevanceScore?: number;
    isAnalyzing?: boolean;
    assessment?: string;
    citations?: string;
  }[];
}

interface DocumentCardProps {
  document: RequiredDocument;
  onUpload: (doc: RequiredDocument) => void;
  onDownloadSample: (url: string) => void;
  onDownloadTemplate: (url: string) => void;
  onViewAssessment: (doc: RequiredDocument['uploadedDocs'][0]) => void;
}

export const DocumentCard = ({ 
  document, 
  onUpload, 
  onDownloadSample, 
  onDownloadTemplate,
  onViewAssessment 
}: DocumentCardProps) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <CardTitle className="text-lg">{document.title}</CardTitle>
              {document.required && (
                <Badge variant="destructive" className="text-xs">Required</Badge>
              )}
              {document.uploadedDocs.length > 0 && (
                <Badge variant="default" className="text-xs bg-green-100 text-green-800">
                  {document.uploadedDocs.length} uploaded
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{document.description}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button 
              size="sm" 
              onClick={() => onUpload(document)}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Upload Document
            </Button>
            
            {document.sampleUrl && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onDownloadSample(document.sampleUrl!)}
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                View Sample
              </Button>
            )}
            
            {document.templateUrl && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onDownloadTemplate(document.templateUrl!)}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download Template
              </Button>
            )}
          </div>

          {/* Uploaded Documents */}
          {document.uploadedDocs.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Uploaded Documents:</h4>
              <div className="space-y-2">
                {document.uploadedDocs.map((uploadedDoc) => (
                  <UploadedDocumentItem
                    key={uploadedDoc.id}
                    document={uploadedDoc}
                    onViewAssessment={onViewAssessment}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
